import { ScreenIcon } from "./ScreenIcon";
import { useState, type ReactNode } from "react";
import { useSaveRegs, pendingDrafts, restoreDraft, discardDraft } from "../lib/saveBar";
import { draftAgeText } from "../lib/screenDraft";

/**
 * 관리자 공통 UI 조각 — 화면마다 다르게 생겼던 것을 하나로.
 * 새 패널을 만들 때는 여기 있는 것부터 쓴다(안 그러면 색·크기가 또 갈린다).
 */

export type MsgKind = "ok" | "warn" | "err" | "info";

/** 결과 알림. kind 를 안 주면 문구 앞머리로 짐작한다(패널들이 ✅·⚠️·↩️ 를 일관되게 쓴다). */
export function Msg({ text, kind }: { text: string | null | undefined; kind?: MsgKind }) {
  if (!text) return null;
  return (
    <p className="msg" data-kind={kind ?? guessKind(text)} role="status">
      {text}
    </p>
  );
}
function guessKind(t: string): MsgKind {
  if (/^(⚠️|❌|⚠️)/.test(t) || /못했|실패|연결하지/.test(t)) return "err";
  if (/^(↩️|🤖|↩)/.test(t)) return "info";
  return "ok";
}

/** 아직 아무것도 없을 때. "없다"로 끝내지 말고 **무엇을 하면 되는지**까지 말한다. */
export function Empty({ icon = "🫧", children, action }: { icon?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty__ico" aria-hidden="true">{icon}</span>
      <div>{children}</div>
      {action ? <div className="empty__do">{action}</div> : null}
    </div>
  );
}

/** 화면 제목 — 사이드바·설명줄·패널 제목으로 세 번 나오던 것을 한 번으로. */
export function PageHead({ icon, art, title, desc }: { icon: string; art?: string; title: string; desc: string }) {
  return (
    <header className="phead">
      <h1>
        {art ? <ScreenIcon art={art} emoji={icon} size={30} /> : <span aria-hidden="true">{icon}</span>} {title}
      </h1>
      <p>{desc}</p>
    </header>
  );
}

/**
 * 하단 고정 저장바 — 등록된 저장 동작을 모아 **버튼 하나**로 실행한다.
 * 여러 패널이 등록돼 있으면(행사·랠리) 바뀐 것만 차례로 저장한다.
 */
export function SaveBar() {
  const regs = useSaveRegs();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  if (regs.length === 0) return null;

  // dirty 를 안 준 패널은 "셀 수 없음" — 항상 누를 수 있어야 한다.
  const counted = regs.filter((r) => typeof r.dirty === "number");
  const total = counted.reduce((n, r) => n + (r.dirty ?? 0), 0);
  const uncounted = regs.length - counted.length;
  const canSave = uncounted > 0 || total > 0;

  async function onSave() {
    setBusy(true);
    setNote(null);
    const todo = regs.filter((r) => typeof r.dirty !== "number" || (r.dirty ?? 0) > 0);
    const failed: string[] = [];
    for (const r of todo) {
      try {
        await r.save();
      } catch (e) {
        // 실패를 삼키지 않는다 — 어느 것이 왜 안 올라갔는지 운영자가 알아야 다시 시도한다.
        failed.push(`${r.label}(${(e as Error).message})`);
      }
    }
    setBusy(false);
    setNote(failed.length ? `⚠️ 못 올린 것: ${failed.join(", ")}` : "✅ 웹에 올렸어요");
    if (!failed.length) window.setTimeout(() => setNote(null), 3000);
  }

  const drafters = regs.filter((r) => r.saveDraft);
  /** 이 화면에 불러올 보관본이 있나. regs 가 바뀔 때마다 다시 본다(화면을 옮기면 달라진다). */
  const drafts = pendingDrafts();

  function onRestore() {
    const done = drafts.filter((d) => restoreDraft(d.id)).map((d) => d.label);
    setNote(done.length ? `💾 ${done.join(" · ")} 을(를) 불러왔어요` : "⚠️ 불러오지 못했어요");
    window.setTimeout(() => setNote(null), 4000);
  }

  function onDiscard() {
    if (!window.confirm("저장해 둔 작업을 버릴까요? 되돌릴 수 없어요.")) return;
    drafts.forEach((d) => discardDraft(d.id));
    setNote("저장해 둔 것을 버렸어요");
    window.setTimeout(() => setNote(null), 3000);
  }
  /** 웹에는 안 올리고 하던 일만 저장. 실패해도 편집을 막지 않는다 — 무슨 일이 났는지만 적는다. */
  async function onDraft() {
    setBusy(true);
    setNote(null);
    const failed: string[] = [];
    for (const r of drafters) {
      try {
        await r.saveDraft!();
      } catch (e) {
        /**
         * 🔴 **이유를 삼키지 않는다.** 예전엔 화면 이름만 적어서 「⚠️ 저장 못 함: 읽어주기」가
         * 전부였다. 운영자도, 이걸 고치러 온 사람도 왜인지 알 수가 없다 —
         * 저장소가 꽉 찬 건지, 사생활 보호 모드인지, 코드가 터진 건지.
         * 실제로 배선 버그(undefined 호출)를 이 한 줄이 가리고 있었다(2026-08-23).
         */
        failed.push(`${r.label}(${(e as Error).message || "알 수 없는 이유"})`);
      }
    }
    setBusy(false);
    setNote(
      failed.length
        ? `⚠️ 저장 못 함: ${failed.join(", ")}`
        : "💾 이 기기에 저장했어요 (웹에는 아직 안 올라갔어요)",
    );
    if (!failed.length) window.setTimeout(() => setNote(null), 4000);
  }

  return (
    <div className="savebar" role="region" aria-label="변경 사항 올리기">
      {/* 💾 저장해 둔 게 있으면 **먼저 알려준다.**
          안 알려주면 운영자는 어제 저장한 걸 잊고 처음부터 다시 만든다 — 보관함이 있어도
          모르면 없는 것과 같다. 불러온 뒤에도 보관본은 안 지운다(실수로 눌렀을 수 있다). */}
      {drafts.length > 0 ? (
        <div className="savebar__resume">
          <span>
            💾 저장해 둔 <b>{drafts.map((d) => d.label).join(" · ")}</b> 이(가) 있어요
            <small> ({draftAgeText(drafts[0]!.at)} · 이 기기)</small>
          </span>
          <button type="button" className="btn btn--sm" onClick={onRestore} disabled={busy}>
            불러오기
          </button>
          <button type="button" className="btn btn--sm" onClick={onDiscard} disabled={busy}>
            버리기
          </button>
        </div>
      ) : null}
      <div className="savebar__inner">
        <span className="savebar__state">
          {total > 0 ? (
            <>
              <b className="savebar__dot" aria-hidden="true" />
              바꾼 것 <b>{total}</b>개
            </>
          ) : uncounted > 0 ? (
            <>{regs.map((r) => r.label).join(" · ")}</>
          ) : (
            <span className="savebar__clean">바꾼 것 없음</span>
          )}
        </span>
        {note ? <span className="savebar__note">{note}</span> : null}
        {/* 💾 하던 일만 저장 — 아이 화면은 그대로 둔다.
            전자책처럼 한 번에 못 끝내는 작업 때문에 생겼다: 저장하는 길이 [웹에 올리기] 하나뿐이라
            중간 저장이 곧 **미완성 게시**였다. 이 버튼이 있는 화면에서만 보인다. */}
        {drafters.length > 0 ? (
          <button
            type="button"
            className="btn savebar__draft"
            onClick={() => void onDraft()}
            disabled={busy}
            title="웹에는 올리지 않고, 이 기기에 하던 일만 저장해요"
          >
            {busy ? "저장 중…" : "💾 저장하기"}
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn--primary savebar__go"
          onClick={() => void onSave()}
          disabled={busy || !canSave}
        >
          {busy ? "올리는 중…" : "🚀 웹에 올리기"}
        </button>
      </div>
    </div>
  );
}
